import React, { useCallback, useMemo, useRef } from 'react';

import { Image, Platform, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native-gesture-handler';
import LinearGradient from 'react-native-linear-gradient';
import { useSelector } from 'react-redux';

import { MoreInfoIcon, lockedContentCenter } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    ASSET_CONTENT_MARKER_ID,
    ASSET_METADATA_ID,
    ASSET_PARENTAL_RATING,
    ASSET_SEASON_EPISODE_NUMBER_ID,
    ASSET_TITLE_ID,
    CHANNEL_LOGO_ID,
    MORE_INFO_BUTTON_ID,
} from 'config/testID';

import { MixedItem } from 'store/railsBuilder/reducer.types';
import { selectEntitlements } from 'store/user/selectors';

import { isAssetVod } from 'utils/checkVodType';
import isFunction from 'utils/function';
import getOrdinal from 'utils/getOrdinary';
import getVodStripesExpiryDate from 'utils/getVodStripesExpiryDate';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';

import BaseTileImage from 'components/BaseTileImage';
import ContentMarkerIcons from 'components/ContentMarkerIcons';
import ActionButton from 'components/CustomButtons/ActionButton';
import useAccessibilityFocus from 'components/Gallery/hooks/useAccessibilityFocus';
import useGallery from 'components/Gallery/hooks/useGallery';
import Marker from 'components/Marker';
import { AspectRatio, ImageType } from 'components/NagraImage/types';
import { PLACEHOLDER_ICON_CENTER } from 'components/Ribbon/constants';
import { OnUavAnalyticsProps, StandardUavProps } from 'components/Ribbon/types';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import ChannelLogo from '../ChannelLogo';
import { verticalGradient } from '../Gradient';
import styles from './styles';

export type Props = {
    item: MixedItem;
    focused: boolean;
    index: number;
    onPress(item: MixedItem, index: number): void;
    onShowPinDialog?(): void;
    onSetParentalControlCheckedCallback?(
        callback: undefined | (() => void),
    ): void;
    totalNumberOfItems: number;
} & StandardUavProps &
    OnUavAnalyticsProps;

const {
    SCREEN_GALLERY_MORE_INFO_BUTTON,
    CARDS_TITLE_CONTENT_LOCKED,
    ADULT_LOCKED_CONTENT_DESCRIPTION,
    ACCESSIBILITY: {
        PLAYER: { ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE },
        GALLERY: { ACCESSIBILITY_GALLERY_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

function PhoneView({
    item,
    focused,
    onPress,
    onDeepLinkTriggered,
    onThirdPartyAppStart,
    onRailsSelection,
    railId,
    templateId,
    index,
    onShowPinDialog,
    onSetParentalControlCheckedCallback,
    totalNumberOfItems,
}: Props) {
    const {
        id,
        title,
        channelId,
        contentType,
        startTime,
        endTime,
        vodProductIds,
        playbackType,
        vodStartTime,
    } = item;
    const {
        width,
        primary,
        isCatchupAsset,
        ableToStartOver,
        metadata,
        seriesMetadata,
        parentalRating,
        handlePress,
        isValidDeeplinkItem,
        shouldDisplaySubscribeIcon,
    } = useGallery({
        item,
        onPress,
        onThirdPartyAppStart,
        onRailsSelection,
        onDeepLinkTriggered,
        railId,
        templateId,
        index,
    });
    const { t } = useTranslation();
    const accessibleViewRef = useRef(null);

    const isVod = isAssetVod(playbackType);

    const profileParentalControlRating = useProfileParentalRating();
    const isContentLocked = needsToLockContent(
        profileParentalControlRating,
        parentalRating,
    );

    const entitlements = useSelector(selectEntitlements);

    const vodComingSoonDate = isVod && vodStartTime ? vodStartTime : undefined;

    const vodExpiryDate = useMemo(() => {
        if (isVod) {
            return getVodStripesExpiryDate(entitlements, vodProductIds);
        }

        return undefined;
    }, [entitlements, vodProductIds, isVod]);

    const onHandlePress = useCallback(() => {
        if (isContentLocked && isFunction(onShowPinDialog)) {
            if (isFunction(onSetParentalControlCheckedCallback)) {
                onSetParentalControlCheckedCallback(handlePress);
            }

            onShowPinDialog();
        } else {
            handlePress();
        }
    }, [
        isContentLocked,
        onShowPinDialog,
        handlePress,
        onSetParentalControlCheckedCallback,
    ]);

    const accessibilityLabel = useMemo(() => {
        if (isContentLocked && index === 0) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(
                ACCESSIBILITY_GALLERY_FIRST_ASSET,
                {
                    number: totalNumberOfItems,
                    interpolation: {
                        prefix: '%',
                        suffix: '%',
                        escapeValue: false,
                    },
                },
            )}`;
        }

        if (isContentLocked) {
            return `${t(CARDS_TITLE_CONTENT_LOCKED)} ${t(
                'ACCESSIBILITY_GALLERY_%NUMBER%_ASSET',
                {
                    number: getOrdinal(index + 1),
                    interpolation: {
                        prefix: '%',
                        suffix: '%',
                        escapeValue: false,
                    },
                },
            )}`;
        }

        if (index === 0) {
            return `${title} ${t(ACCESSIBILITY_GALLERY_FIRST_ASSET, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })}`;
        }

        return `${title} ${t('ACCESSIBILITY_GALLERY_%NUMBER%_ASSET', {
            number: getOrdinal(index + 1),
            interpolation: {
                prefix: '%',
                suffix: '%',
                escapeValue: false,
            },
        })}`;
    }, [isContentLocked, t, totalNumberOfItems, index, title]);

    useAccessibilityFocus({ accessibleViewRef, focused });

    return (
        <TouchableOpacity
            style={styles.container}
            activeOpacity={1}
            importantForAccessibility="no-hide-descendants"
            accessibilityElementsHidden
            onPress={onHandlePress}
        >
            <View style={[gStyles.flex, styles.imageContainer]}>
                <View style={gStyles.flex} accessibilityElementsHidden>
                    {isContentLocked ? (
                        <Image
                            source={lockedContentCenter}
                            style={styles.image}
                            resizeMode="cover"
                            width={width}
                        />
                    ) : (
                        <BaseTileImage
                            style={styles.image}
                            placeHolder={PLACEHOLDER_ICON_CENTER}
                            backupSourceResizeMode="contain"
                            resizeMode="cover"
                            contentId={id}
                            contentType={contentType}
                            imageType={ImageType.GALLERY}
                            aspect={AspectRatio.LANDSCAPE}
                            width={width}
                        />
                    )}
                    <LinearGradient
                        style={[styles.linerGradient, styles.gradientTopPhone]}
                        colors={verticalGradient.colors}
                        start={verticalGradient.translucent}
                        end={verticalGradient.opaque}
                        pointerEvents="none"
                    />
                    <LinearGradient
                        style={[
                            styles.linerGradient,
                            styles.gradientBottomPhone,
                        ]}
                        colors={verticalGradient.colors}
                        start={verticalGradient.opaque}
                        end={verticalGradient.translucent}
                        pointerEvents="none"
                    />
                </View>
            </View>
            <View
                ref={accessibleViewRef}
                style={styles.accessibilityContainer}
                accessible={focused}
                accessibilityLabel={accessibilityLabel}
                accessibilityElementsHidden={!focused}
                importantForAccessibility={
                    focused ? 'auto' : 'no-hide-descendants'
                }
                accessibilityHint={Platform.select({
                    default: t(ACCESSIBILITY_PLAYER_OPEN_DETAIL_PAGE),
                    android: '',
                })}
            >
                {!!channelId && (
                    <ChannelLogo
                        testID={CHANNEL_LOGO_ID}
                        channelId={channelId}
                    />
                )}
                <Text
                    testID={ASSET_TITLE_ID}
                    style={styles.titleText}
                    category={ETextCategory.title1}
                    color={ETextColor.neutral}
                    numberOfLines={3}
                    accessibilityElementsHidden
                >
                    {isContentLocked ? t(CARDS_TITLE_CONTENT_LOCKED) : title}
                </Text>
                {!!seriesMetadata && (
                    <Text
                        testID={ASSET_SEASON_EPISODE_NUMBER_ID}
                        numberOfLines={1}
                        style={styles.seriesMetadata}
                        category={ETextCategory.title3}
                        color={ETextColor.neutral}
                        accessibilityElementsHidden
                    >
                        {seriesMetadata}
                    </Text>
                )}
                <View
                    style={styles.markerContainer}
                    accessibilityElementsHidden
                >
                    {startTime && endTime && (
                        <Marker
                            testID={ASSET_CONTENT_MARKER_ID}
                            startTime={startTime}
                            endTime={endTime}
                            bgColor={primary}
                            style={styles.marker}
                            expiryDate={vodExpiryDate}
                            comingSoonDate={vodComingSoonDate}
                        />
                    )}
                    <ContentMarkerIcons
                        parentalRatingTestID={ASSET_PARENTAL_RATING}
                        shouldDisplayCatchup={
                            isCatchupAsset || isValidDeeplinkItem
                        }
                        parentalRating={parentalRating}
                        iconStyle={styles.markerIcons}
                        shouldDisplayStartOver={ableToStartOver}
                        isContentLocked={isContentLocked}
                        shouldDisplaySubscribe={shouldDisplaySubscribeIcon}
                        importantForAccessibility="no-hide-descendants"
                    />
                </View>
                {!!metadata && (
                    <Text
                        testID={ASSET_METADATA_ID}
                        style={styles.metadataText}
                        category={ETextCategory.body2}
                        color={ETextColor.light_grey}
                        accessibilityElementsHidden
                    >
                        {isContentLocked
                            ? t(ADULT_LOCKED_CONTENT_DESCRIPTION)
                            : metadata}
                    </Text>
                )}
                <ActionButton
                    testID={MORE_INFO_BUTTON_ID}
                    title={SCREEN_GALLERY_MORE_INFO_BUTTON}
                    fillColor={primary}
                    Icon={MoreInfoIcon}
                    buttonTextStyle={styles.moreInfoText}
                    accessible={false}
                    importantForAccessibility="no-hide-descendants"
                    accessibilityElementsHidden
                />
            </View>
        </TouchableOpacity>
    );
}

export default PhoneView;
